// src/pages/Users/UserList.jsx
// -----------------------------------------------------------------------------
// Admin screen to manage system users (Requirement #4, Requirement #24).
// Full CRUD operations built with TableList, UserForm, DeleteConfirmModal,
// and custom hook useUserData.
// -----------------------------------------------------------------------------
import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import useUserData from '../../hooks/useUserData';
import userService from '../../api/userService';
import Modal from '../../components/common/Modal';
import TableList from '../../components/common/TableList';
import UserForm from './UserForm';
import DeleteConfirmModal from '../../components/common/DeleteConfirmModal';
import StatusBadge from '../../components/common/StatusBadge';
import roleService from '../../api/roleService';
import clientOrgService from '../../api/clientOrgService';

export default function UserList() {
  // Initialize custom CRUD Hook for Users pointing to real backend /Auth/get-all
  const {
    data,
    paginatedData,
    loading,
    submitting,
    deleting,
    serverErrors,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    totalRecords,
    isFormOpen,
    isDeleteOpen,
    formMode,
    selectedRecord,
    openCreateModal,
    openEditModal,
    closeFormModal,
    openDeleteModal,
    closeDeleteModal,
    handleSave,
    handleDelete,
    refetch,
  } = useUserData();

  // ---------- Offboarding (deactivate / reactivate / unlock) ----------
  const [deactivateTarget, setDeactivateTarget] = useState(null);
  const [reassignToUserId, setReassignToUserId] = useState('');
  const [statusActionBusy, setStatusActionBusy] = useState(false);

  const fullName = (u) => `${u?.first_Name || ''} ${u?.last_Name || ''}`.trim() || u?.email || `#${u?.id}`;
  const isInactive = (u) => (u?.status || '').toLowerCase() === 'inactive';
  const isLocked = (u) =>
    (u?.status || '').toLowerCase() === 'locked' ||
    (u?.account_Locked_Until_Dttm && new Date(u.account_Locked_Until_Dttm) > new Date());

  // API returns 200 + a plain string for validation failures.
  const runStatusAction = async (action, successMessage) => {
    setStatusActionBusy(true);
    try {
      const res = await action();
      if (typeof res?.data === 'string' && !/success|unlocked/i.test(res.data)) {
        throw new Error(res.data);
      }
      const reassigned = res?.data?.ticketsReassigned;
      toast.success(reassigned ? `${successMessage} ${reassigned} open ticket(s) reassigned.` : successMessage);
      await refetch();
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.response?.data || err.message || 'Action failed.');
      return false;
    } finally {
      setStatusActionBusy(false);
    }
  };

  const handleConfirmDeactivate = async () => {
    const ok = await runStatusAction(
      () =>
        userService.deactivate(deactivateTarget.id, {
          reassignToUserId: reassignToUserId ? Number(reassignToUserId) : null,
        }),
      'User deactivated.'
    );
    if (ok) {
      setDeactivateTarget(null);
      setReassignToUserId('');
    }
  };

  const renderStatusActions = (row) => (
    <>
      {isLocked(row) && (
        <button
          className="btn-table-action"
          disabled={statusActionBusy}
          onClick={() => runStatusAction(() => userService.unlock(row.id), 'Account unlocked.')}
          title="Clear the failed-login lockout"
        >
          Unlock
        </button>
      )}
      {isInactive(row) ? (
        <button
          className="btn-table-action"
          disabled={statusActionBusy}
          onClick={() => runStatusAction(() => userService.reactivate(row.id), 'User reactivated.')}
          title="Allow this user to log in again"
        >
          Reactivate
        </button>
      ) : (
        <button
          className="btn-table-action"
          disabled={statusActionBusy}
          onClick={() => setDeactivateTarget(row)}
          title="Block login and hand over open tickets"
        >
          Deactivate
        </button>
      )}
    </>
  );

  const [roles, setRoles] = useState([]);
  const [clientOrgs, setClientOrgs] = useState([]);

  useEffect(() => {
    roleService.getAll()
      .then((res) => {
        setRoles(res?.data || []);
      })
      .catch((err) => {
        console.error("Failed to fetch roles:", err);
      });

    clientOrgService.getAll()
      .then((res) => {
        setClientOrgs(res?.data || []);
      })
      .catch((err) => {
        console.error("Failed to fetch client organizations:", err);
      });
  }, []);

  // Dynamic fields configuration matching the real Auth register DTO schema
  const userFields = useMemo(() => {
    const isEditMode = formMode === 'edit';

    const fields = [
      //AFIX_SIT_004
      // Bug	Users	Required fields validations are missing 
      {
        name: 'first_Name',
        label: 'First Name',
        type: 'varchar',
        required: true,
        maxLength: 50,
        placeholder: 'e.g. John',
      },
      {
        name: 'last_Name',
        label: 'Last Name',
        type: 'varchar',
        required: true,
        maxLength: 50,
        placeholder: 'e.g. Doe',
      },
      {
        name: 'email',
        label: 'Email',
        type: 'email',
        required: true,
        maxLength: 100,
        placeholder: 'e.g. john.doe@company.com',
      },
      //AFIX_SIT_004
      {
        name: 'phone',
        label: 'Phone',
        type: 'phone',
        maxLength: 20,
        placeholder: 'e.g. 8975850938',
      },
      //AFIX_SIT_004
      // Bug	Users	Required fields validations are missing 
      {
        name: 'role_ID',
        label: 'Role',
        type: 'select',
        required: true,
        options: roles.map((r) => ({ label: r.roleName, value: r.roleID })),
      },
      //AFIX_SIT_004
      //AFIX_SIT_005
      // Bug	Users	Remove None option from Client organization field 
      {
        name: 'client_Org_ID',
        label: 'Client Organization',
        type: 'select',
        options: clientOrgs.map((c) => ({ label: c.orgName, value: c.clientOrgID })),
      },
      //AFIX_SIT_005
    ];

    if (isEditMode) {
      fields.unshift({
        name: 'id',
        label: 'User ID',
        type: 'integer',
        isPrimaryKey: true,
        readOnly: true,
      });
    }

    return fields;
  }, [formMode, roles, clientOrgs]);

  // Data grid columns mapping to API response DTO keys
  const columns = [
    { key: 'id', label: 'User ID', sortable: true, width: '100px' },
    {
      key: 'name',
      label: 'Full Name',
      sortable: true,
      render: (row) => `${row.first_Name || ''} ${row.last_Name || ''}`.trim() || '-',
    },
    { key: 'email', label: 'Email', sortable: true },
    {
      key: 'phone',
      label: 'Phone',
      sortable: true,
      render: (row) => row.phone || row.phone_1 || '-',
    },
    {
      key: 'role',
      label: 'Role',
      sortable: true,
      render: (row) => {
        const found = roles.find((r) => Number(r.roleID) === Number(row.role_ID) || Number(r.roleID) === Number(row.roleID));
        return found ? found.roleName : (row.role || row.role_ID || '-');
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (row) => (
        <StatusBadge
          value={
            row.status ||
            row.Status ||
            row.userStatus ||
            row.statusName ||
            (row.isActive !== undefined ? (row.isActive ? 'Active' : 'Inactive') : '')
          }
        />
      ),
    },
  ];

  const handleFormSubmit = async (formData) => {
    const mappedData = {
      id: formMode === 'edit' ? Number(selectedRecord.id) : 0,
      username: formData.email,
      email: formData.email,
      first_Name: formData.first_Name,
      last_Name: formData.last_Name,
      phone: formData.phone || '',
      role_ID: formData.role_ID ? Number(formData.role_ID) : 0,
      client_Org_ID: formData.client_Org_ID ? Number(formData.client_Org_ID) : null,
    };
    await handleSave(mappedData);
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Users</h1>
          <div className="subtitle">
            Manage system operators, role configurations, and address profiles.
          </div>
        </div>
        <button className="btn btn-primary" onClick={openCreateModal}>
          + Add User
        </button>
      </div>

      <TableList
        columns={columns}
        data={data}
        paginatedData={paginatedData}
        loading={loading}
        idKey="id"
        emptyMessage="No users found."
        // Search & Filter
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        // Sorting
        sortConfig={sortConfig}
        onSort={handleSort}
        // Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalRecords={totalRecords}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={setPageSize}
        // Actions
        onEdit={openEditModal}
        onDelete={openDeleteModal}
        renderActions={renderStatusActions}
      />

      {/* Deactivate (offboarding) dialog */}
      {deactivateTarget && (
        <Modal title={`Deactivate ${fullName(deactivateTarget)}`} onClose={() => setDeactivateTarget(null)} size="small">
          <p>
            This user will no longer be able to log in. Their current assignments on open tickets
            will be closed.
          </p>
          <div className="form-group">
            <label htmlFor="reassignTo">Reassign open tickets to (optional)</label>
            <select
              id="reassignTo"
              value={reassignToUserId}
              onChange={(e) => setReassignToUserId(e.target.value)}
            >
              <option value="">Do not reassign</option>
              {data
                .filter((u) => u.id !== deactivateTarget.id && !isInactive(u))
                .map((u) => (
                  <option key={u.id} value={u.id}>
                    {fullName(u)} ({u.email})
                  </option>
                ))}
            </select>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button className="btn btn-outline" onClick={() => setDeactivateTarget(null)} disabled={statusActionBusy}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleConfirmDeactivate} disabled={statusActionBusy}>
              {statusActionBusy ? 'Deactivating...' : 'Deactivate'}
            </button>
          </div>
        </Modal>
      )}

      {/* Conditionally mount form to ensure state resets */}
      {isFormOpen && (
        <UserForm
          fields={userFields}
          initialValues={
            selectedRecord
              ? {
                  ...selectedRecord,
                  phone: selectedRecord.phone || '',
                  role_ID: selectedRecord.role_ID ?? selectedRecord.roleID ?? '',
                  client_Org_ID: selectedRecord.client_Org_ID ?? selectedRecord.clientOrgID ?? '',
                }
              : {
                  first_Name: '',
                  last_Name: '',
                  email: '',
                  phone: '',
                  role_ID: '',
                  client_Org_ID: '',
                }
          }
          mode={formMode}
          isOpen={isFormOpen}
          onSubmit={handleFormSubmit}
          onCancel={closeFormModal}
          submitting={submitting}
          serverErrors={serverErrors}
          itemName="User"
        />
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmModal
        isOpen={isDeleteOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        title="Delete User"
        itemLabel={selectedRecord ? `${selectedRecord.first_Name} ${selectedRecord.last_Name}` : 'User'}
        deleting={deleting}
      />
    </div>
  );
}